import type { HighlightAdapter } from './highlight-adapter.js'
import { selectionService } from './selection-service.js'
import { featureRefKey, type SelectableFeatureRef } from './selectable-feature-ref.js'

class HighlightManager {
  private adapters: HighlightAdapter[] = []
  private unsubscribe: (() => void) | undefined
  // Last-applied signature per adapter, so a change touching only one source
  // (e.g. selecting an OSM feature) doesn't re-run every adapter's highlight —
  // a COTREX setFilter / full re-render is expensive.
  private lastSig = new WeakMap<HighlightAdapter, string>()

  registerAdapter(adapter: HighlightAdapter): void {
    this.adapters.push(adapter)
  }

  unregisterAdapter(adapter: HighlightAdapter): void {
    this.adapters = this.adapters.filter(a => a !== adapter)
    this.lastSig.delete(adapter)
  }

  connect(): void {
    this.unsubscribe = selectionService.onChange(
      (selected: ReadonlyMap<string, SelectableFeatureRef>) => {
        for (const adapter of this.adapters) {
          const relevant = [...selected.values()].filter(ref =>
            adapter.sources.includes(ref.source)
          )
          const sig = relevant.map(featureRefKey).sort().join('|')
          if (this.lastSig.get(adapter) === sig) continue
          this.lastSig.set(adapter, sig)
          if (relevant.length > 0) {
            adapter.applyHighlights(relevant)
          } else {
            adapter.clearHighlights()
          }
        }
      }
    )
  }

  disconnect(): void {
    this.unsubscribe?.()
    this.unsubscribe = undefined
    this.lastSig = new WeakMap()
    for (const adapter of this.adapters) {
      adapter.clearHighlights()
    }
  }
}

export const highlightManager = new HighlightManager()
